import { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Award, Clock, Download, FileSpreadsheet, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { normalizeNote, moyenneNormalisee } from '../utils/normalizeNote';

const ResponsableAnalytics = ({ user }) => {
  const [stats, setStats] = useState({
    stagiaires: 0,
    formateurs: 0,
    tuteurs: 0,
    moyenne_generale: 0,
    total_evaluations: 0,
    moyennes_categories: {},
    evolution_notes: [],
    presences_stats: {}
  });
  const [centreNom, setCentreNom] = useState('');
  const [stagiaires, setStagiaires] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [competencesStats, setCompetencesStats] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [stagRes, formRes, tutRes, centreRes, compStatsRes] = await Promise.all([
        axios.get('/responsable/stagiaires'),
        axios.get('/responsable/formateurs'),
        axios.get('/responsable/tuteurs'),
        axios.get('/responsable/mon-centre'),
        axios.get('/competences/stats', { params: { centre: 'current' } }).catch(() => ({ data: null }))
      ]);

      const stagiairesData = stagRes.data || [];
      const formateursData = formRes.data || [];
      const tuteursData = tutRes.data || [];
      
      setStagiaires(stagiairesData);
      setCentreNom(centreRes.data?.nom || '');
      setCompetencesStats(compStatsRes.data);

      // Récupérer toutes les notes des stagiaires du centre
      const allNotes = [];
      for (const stag of stagiairesData) {
        try {
          const notesRes = await axios.get(`/notes/stagiaire/${stag.user_id}`);
          allNotes.push(...notesRes.data.map(n => ({ 
            ...n, 
            stagiaire_id: stag.user_id,
            stagiaire_nom: `${stag.user_info?.prenom} ${stag.user_info?.nom}` 
          })));
        } catch (e) {
        }
      }

      setNotes(allNotes);

      const moyenne = allNotes.length > 0
        ? moyenneNormalisee(allNotes)
        : 0;

      // Moyennes par catégorie
      const moyennesCategories = allNotes.reduce((acc, n) => {
        if (!acc[n.categorie]) acc[n.categorie] = [];
        acc[n.categorie].push(n);
        return acc;
      }, {});

      const moyennesCat = Object.entries(moyennesCategories).reduce((acc, [cat, notes]) => {
        acc[cat] = parseFloat(moyenneNormalisee(notes).toFixed(2));
        return acc;
      }, {});

      // Évolution des notes (par mois)
      const notesByMonth = allNotes.reduce((acc, n) => {
        const month = new Date(n.date).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
        if (!acc[month]) acc[month] = [];
        acc[month].push(n);
        return acc;
      }, {});

      const evolution = Object.entries(notesByMonth)
        .map(([month, notes]) => ({
          mois: month,
          moyenne: parseFloat(moyenneNormalisee(notes).toFixed(1))
        }))
        .slice(-6);

      // Stats présences (récupérer les vraies données)
      let presencesStats = { present: 0, absent: 0, retard: 0 };
      try {
        const presRes = await axios.get('/responsable/presences');
        const presences = presRes.data || [];
        presencesStats = {
          present: presences.filter(p => p.statut === 'present').length,
          absent: presences.filter(p => p.statut === 'absent').length,
          retard: presences.filter(p => p.statut === 'retard').length
        };
      } catch (e) {
      }

      setStats({
        stagiaires: stagiairesData.length,
        formateurs: formateursData.length,
        tuteurs: tuteursData.length,
        moyenne_generale: moyenne.toFixed(2),
        total_evaluations: allNotes.length,
        moyennes_categories: moyennesCat,
        evolution_notes: evolution,
        presences_stats: presencesStats
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleExportNotes = async () => {
    try {
      toast.info('Génération du fichier Excel...');
      // Créer un fichier Excel simple avec les notes
      const data = notes.map(n => ({
        Stagiaire: n.stagiaire_nom,
        Catégorie: n.categorie,
        Item: n.item,
        Note: parseFloat(normalizeNote(n).toFixed(1)),
        Date: new Date(n.date).toLocaleDateString('fr-FR'),
        Commentaire: n.commentaire || ''
      }));
      
      // Pour l'instant, on utilise un export simple
      const csvContent = [
        Object.keys(data[0] || {}).join(';'),
        ...data.map(row => Object.values(row).join(';'))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `notes_${centreNom}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Notes exportées');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Chargement des analytics...</div>;
  }

  const COLORS = ['#8DC63F', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

  const moyennesData = Object.entries(stats.moyennes_categories).map(([name, value]) => ({ name, moyenne: parseFloat(value) }));
  const presencesData = Object.entries(stats.presences_stats).map(([name, value]) => ({ 
    name: name === 'present' ? 'Présents' : name === 'absent' ? 'Absents' : 'Retards', 
    value 
  }));

  return (
    <div className="space-y-8" data-testid="responsable-analytics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Consultation</span>
          </div>
          <h1 className="text-3xl font-display font-bold text-[#0F172A] mb-2">Analytics - Centre {centreNom}</h1>
          <p className="text-slate-600">Tableau de bord analytique du centre de formation</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportNotes} variant="outline" size="sm" data-testid="export-notes">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Exporter Notes
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-[#8DC63F]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Stagiaires</p>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats.stagiaires}</p>
            </div>
            <Users className="h-8 w-8 text-[#8DC63F]" />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Moyenne Générale</p>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats.moyenne_generale}/10</p>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Évaluations</p>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats.total_evaluations}</p>
            </div>
            <Award className="h-8 w-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Formateurs + Tuteurs</p>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats.formateurs + stats.tuteurs}</p>
            </div>
            <Users className="h-8 w-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Statistiques des Compétences */}
      {competencesStats && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Statistiques des Compétences</h2>
          
          {/* KPIs Compétences */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
              <p className="text-3xl font-bold text-green-600">{competencesStats.stats_by_statut?.competent || 0}</p>
              <p className="text-sm text-green-700">Compétent</p>
              <p className="text-xs text-green-600">{competencesStats.pourcentages?.competent || 0}%</p>
            </div>
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg text-center">
              <p className="text-3xl font-bold text-orange-600">{competencesStats.stats_by_statut?.en_cours || 0}</p>
              <p className="text-sm text-orange-700">En cours</p>
              <p className="text-xs text-orange-600">{competencesStats.pourcentages?.en_cours || 0}%</p>
            </div>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
              <p className="text-3xl font-bold text-red-600">{competencesStats.stats_by_statut?.non_competent || 0}</p>
              <p className="text-sm text-red-700">Non compétent</p>
              <p className="text-xs text-red-600">{competencesStats.pourcentages?.non_competent || 0}%</p>
            </div>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
              <p className="text-3xl font-bold text-blue-600">{competencesStats.total_evaluations || 0}</p>
              <p className="text-sm text-blue-700">Total évaluations</p>
              <p className="text-xs text-blue-600">{competencesStats.total_stagiaires || 0} stagiaires</p>
            </div>
          </div>

          {/* Graphique Compétences Acquises vs Non Acquises */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Répartition des Compétences</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Acquises', value: competencesStats.stats_by_statut?.competent || 0, fill: '#22c55e' },
                      { name: 'En cours', value: competencesStats.stats_by_statut?.en_cours || 0, fill: '#f59e0b' },
                      { name: 'Non acquises', value: competencesStats.stats_by_statut?.non_competent || 0, fill: '#ef4444' }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {[
                      { name: 'Acquises', fill: '#22c55e' },
                      { name: 'En cours', fill: '#f59e0b' },
                      { name: 'Non acquises', fill: '#ef4444' }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} évaluations`, '']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Moyenne d'heures pour l'autonomie */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-500" />
                Heures d'Encadrement pour l'Autonomie
              </h3>
              <div className="text-center py-4">
                <p className="text-4xl font-bold text-blue-600 mb-1">
                  {competencesStats.moyenne_heures_autonomie 
                    ? `${competencesStats.moyenne_heures_autonomie.toFixed(1)}h`
                    : "N/A"}
                </p>
                <p className="text-sm text-slate-600">Moyenne pour atteindre l'autonomie</p>
                {competencesStats.stagiaires_autonomes !== undefined && (
                  <p className="text-xs text-green-600 mt-2">
                    {competencesStats.stagiaires_autonomes} stagiaire(s) autonome(s) sur {competencesStats.total_stagiaires || 0}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <div className="text-center p-2 bg-white rounded border border-blue-100">
                  <p className="text-lg font-bold text-blue-600">{competencesStats.min_heures_autonomie || 0}h</p>
                  <p className="text-xs text-slate-500">Min</p>
                </div>
                <div className="text-center p-2 bg-white rounded border border-blue-100">
                  <p className="text-lg font-bold text-blue-600">{competencesStats.max_heures_autonomie || 0}h</p>
                  <p className="text-xs text-slate-500">Max</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Top catégories */}
          {competencesStats.top_categories && competencesStats.top_categories.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Top catégories validées</h3>
              <div className="space-y-2">
                {competencesStats.top_categories.map((cat, idx) => (
                  <div key={cat.categorie} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <span className="text-sm font-medium text-slate-700">
                      {idx + 1}. {cat.categorie}
                    </span>
                    <div className="flex gap-3 text-xs">
                      <span className="text-green-600">{cat.competent} validés</span>
                      <span className="text-orange-500">{cat.en_cours} en cours</span>
                      <span className="text-slate-500">{cat.total} total</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Détail des Compétences par Notes (>= 7 = Acquis) */}
      {notes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-[#8DC63F]" />
            Compétences par Notes Formateur
            <span className="text-sm font-normal text-slate-500">(Note ≥ 7 = Acquis)</span>
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compétences Acquises */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                <Award className="h-5 w-5" />
                Compétences Acquises (Note ≥ 7)
              </h3>
              {(() => {
                const competencesGroupees = notes.reduce((acc, n) => {
                  const key = `${n.categorie} - ${n.item}`;
                  if (!acc[key]) {
                    acc[key] = { categorie: n.categorie, item: n.item, notes: [], moyenne: 0 };
                  }
                  acc[key].notes.push(normalizeNote(n));
                  return acc;
                }, {});
                
                Object.values(competencesGroupees).forEach(c => {
                  c.moyenne = c.notes.reduce((a, b) => a + b, 0) / c.notes.length;
                });
                
                const acquises = Object.values(competencesGroupees)
                  .filter(c => c.moyenne >= 7)
                  .sort((a, b) => b.moyenne - a.moyenne);
                
                return acquises.length === 0 ? (
                  <p className="text-green-600 text-sm">Aucune compétence acquise pour le moment</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {acquises.map((c, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-green-100">
                        <div>
                          <p className="font-medium text-sm text-slate-800">{c.item}</p>
                          <p className="text-xs text-slate-500">{c.categorie}</p>
                        </div>
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-sm font-bold rounded">
                          {c.moyenne.toFixed(1)}/10
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            
            {/* Compétences Non Acquises */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                <Target className="h-5 w-5" />
                À Renforcer (Note &lt; 7)
              </h3>
              {(() => {
                const competencesGroupees = notes.reduce((acc, n) => {
                  const key = `${n.categorie} - ${n.item}`;
                  if (!acc[key]) {
                    acc[key] = { categorie: n.categorie, item: n.item, notes: [], moyenne: 0 };
                  }
                  acc[key].notes.push(normalizeNote(n));
                  return acc;
                }, {});
                
                Object.values(competencesGroupees).forEach(c => {
                  c.moyenne = c.notes.reduce((a, b) => a + b, 0) / c.notes.length;
                });
                
                const nonAcquises = Object.values(competencesGroupees)
                  .filter(c => c.moyenne < 7)
                  .sort((a, b) => a.moyenne - b.moyenne);
                
                return nonAcquises.length === 0 ? (
                  <p className="text-red-600 text-sm">Toutes les compétences sont acquises !</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {nonAcquises.map((c, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-red-100">
                        <div>
                          <p className="font-medium text-sm text-slate-800">{c.item}</p>
                          <p className="text-xs text-slate-500">{c.categorie}</p>
                        </div>
                        <span className={`px-2 py-1 text-sm font-bold rounded ${
                          c.moyenne < 4 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {c.moyenne.toFixed(1)}/10
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Moyennes par catégorie */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Moyennes par Catégorie</h2>
          {moyennesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={moyennesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" style={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                <YAxis domain={[0, 10]} />
                <Tooltip />
                <Bar dataKey="moyenne" fill="#8DC63F" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-slate-500 text-center py-12">Aucune donnée</p>
          )}
        </div>

        {/* Statistiques présences */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Présences</h2>
          {presencesData.some(p => p.value > 0) ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={presencesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value">
                  {presencesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={
                      entry.name === 'Présents' ? '#22c55e' : 
                      entry.name === 'Absents' ? '#ef4444' : '#f59e0b'
                    } />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-slate-500 text-center py-12">Aucune donnée de présence</p>
          )}
        </div>
      </div>

      {/* Évolution des notes */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Évolution Moyenne (6 derniers mois)</h2>
        {stats.evolution_notes.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={stats.evolution_notes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="mois" style={{ fontSize: 11 }} />
              <YAxis domain={[0, 10]} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="moyenne" stroke="#8DC63F" strokeWidth={3} name="Moyenne" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-slate-500 text-center py-12">Pas encore de données d'évolution</p>
        )}
      </div>

      {/* Tableau récapitulatif */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Classement des Stagiaires</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Rang</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Stagiaire</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Moyenne</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Nb Éval.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {stagiaires
                .map(stag => {
                  const stagNotes = notes.filter(n => n.stagiaire_id === stag.user_id);
                  const moyenne = stagNotes.length > 0
                    ? moyenneNormalisee(stagNotes)
                    : 0;
                  return { ...stag, moyenne, nbEval: stagNotes.length };
                })
                .sort((a, b) => b.moyenne - a.moyenne)
                .map((stag, idx) => (
                  <tr key={stag.user_id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-medium text-[#0F172A]">#{idx + 1}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {stag.user_info?.prenom} {stag.user_info?.nom}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-[#8DC63F]">{stag.moyenne.toFixed(2)}/10</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{stag.nbEval}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResponsableAnalytics;
